/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Spin, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadInscannedBagsReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const InscannedBagsReport = () => {
    // Set default date range to yesterday and today
    const defaultDateRange = [
        moment().subtract(1, "days"),
        moment()
    ];

    const [loading, setLoading] = useState(false);
    const [hubListLoading, setHubListLoading] = useState(false);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [hubList, setHubList] = useState([]);

    // Filters
    const [inscanHubIds, setInscanHubIds] = useState([]);
    const [bagNumbers, setBagNumbers] = useState("");
    const [isHubDisabled, setIsHubDisabled] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setHubListLoading(true);
                const hubs = await getHubList();

                // Clean up hub labels if they contain the ID
                const cleanedHubs = hubs.map(hub => {
                    const id = String(hub.value);
                    const label = String(hub.label);
                    if (label.startsWith(id)) {
                        const newLabel = label.replace(new RegExp(`^${id}\\s*[-:]?\\s*`), "");
                        return { ...hub, label: newLabel || label };
                    }
                    return hub;
                });

                // Get user info and active hub for auto-selection
                const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userid = JSON.parse(localStorage.getItem("userId") || "{}");
                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "null");
                
                const role = userinfo?.roles?.[0];
                const isHubRestricted = role === "ROLE_HUBADMIN" || role === "ROLE_HUBOPERATOR";
                
                const targetHubId = activeHub?.hubId || userid?.userInfo?.premiseId;

                if (targetHubId) {
                    const hubIdStr = String(targetHubId);
                    const matchingHub = cleanedHubs.find(h => String(h.value) === hubIdStr);
                    
                    if (matchingHub) {
                        setInscanHubIds([matchingHub.value]);
                        if (isHubRestricted) setIsHubDisabled(true);
                    } else if (activeHub?.hubName) {
                        // Fallback: manually add the logged hub if it's not in the fetched list
                        const manualHub = { label: activeHub.hubName, value: targetHubId };
                        cleanedHubs.push(manualHub);
                        setInscanHubIds([targetHubId]);
                        if (isHubRestricted) setIsHubDisabled(true);
                    }
                } else if (isHubRestricted) {
                    // Safety: if restricted but no hub ID found, keep initialization flag
                    setIsHubDisabled(true);
                }

                setHubList(cleanedHubs);
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setHubListLoading(false);
                setIsInitialized(true);
            }
        })();
    }, []);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        if (!isInitialized) return;

        if (dateRange && dateRange[0] && dateRange[1]) {
            fetchReport();
        }
        // eslint-disable-next-line
    }, [dateRange, inscanHubIds, bagNumbers, pagination.current, pagination.pageSize, isInitialized]);


    const fetchReport = async () => {
        const { signal, requestId } = createNewRequest("inscanned-bags-report");

        setLoading(true);
        try {
            const processedBagNumbers = bagNumbers ? bagNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            const params = {
                ...(inscanHubIds?.length > 0 && { inscanHubIds: inscanHubIds.join(",") }),
                ...(processedBagNumbers && { bagNumbers: processedBagNumbers.join(",") }),
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            const reportResponse = await ReportsService.getInscannedBagsReport(params, { signal });
            const { data, total, lastRefreshedAt, nextScheduledRefresh: nextRefresh } = reportResponse;

            if (!isLatestRequest(requestId)) {
                return;
            }

            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextRefresh);
            setPagination(prev => ({
                ...prev,
                total,
            }));
        } catch (error) {
            if (!isLatestRequest(requestId)) return;

            if (error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED") {
                return;
            }

            console.error("Error fetching report data:", error);
            message.error(error.message || "Failed to fetch report data");

            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        try {
            if (!dateRange || !dateRange[0] || !dateRange[1]) {
                message.error("Please select date range for download");
                return;
            }

            const processedBagNumbers = bagNumbers ? bagNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            const downloadParams = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                inscanHubIds: inscanHubIds.length > 0 ? inscanHubIds.join(",") : undefined,
                bagNumbers: processedBagNumbers ? processedBagNumbers.join(",") : undefined
            };

            await downloadInscannedBagsReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to start download");
        }
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Bag Number",
            dataIndex: "bagNumber",
            key: "bagNumber",
            width: 150,
            render: (text) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "Inscan Hub Name", dataIndex: "inscanHubName", key: "inscanHubName", width: 180, render: (text) => text || "---" },
        { title: "Origin Hub Name", dataIndex: "originHubName", key: "originHubName", width: 200, render: (text) => text || "---" },
        { title: "Destination Hub Name", dataIndex: "destinationHubName", key: "destinationHubName", width: 200, render: (text) => text || "---" },
        { title: "Inscan Time", dataIndex: "inscanTime", key: "inscanTime", width: 180, render: (text) => text || "---" },
        { title: "User ID", dataIndex: "userId", key: "userId", width: 120, render: (text) => text || "---" },
        { title: "Inscanned By", dataIndex: "inscannedByUsername", key: "inscannedByUsername", width: 150, render: (text) => text || "---" },
        { title: "Bag Weight", dataIndex: "bagWeight", key: "bagWeight", width: 150, render: (text) => text || "---" },
        { title: "Content Type", dataIndex: "contentType", key: "contentType", width: 150, render: (text) => text || "---" },
        { title: "Mode", dataIndex: "mode", key: "mode", width: 150, render: (text) => text || "---" },
        { title: "Travel By", dataIndex: "travelBy", key: "travelBy", width: 150, render: (text) => text || "---" },
        { title: "Shipment Count", dataIndex: "shipmentCount", key: "shipmentCount", width: 150, render: (text) => text || "---" },



    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Inscanned Bags Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={dateRange}
                            onChange={(val) => {
                                setDateRange(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Inscan Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Inscan Hub"
                            options={hubList}
                            onChange={(val) => {
                                // Handle both single value (when disabled/default mode) and array (multiple mode)
                                setInscanHubIds(Array.isArray(val) ? val : (val ? [val] : []));
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={isHubDisabled ? (inscanHubIds[0] || null) : inscanHubIds}
                            allowClear
                            mode={isHubDisabled ? "default" : "multiple"}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            disabled={isHubDisabled}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Bag Numbers</div>
                        <Input
                            className="custom-input"
                            placeholder="Search Bag Numbers (comma separated)"
                            value={bagNumbers}
                            onChange={(e) => {
                                setBagNumbers(e.target.value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            style={{ width: "100%" }}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Spin spinning={loading}>
                <TableComponent
                    columns={columns}
                    dataSource={reportData}
                    loading={false}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
                    sticky
                />
            </Spin>
        </div>
    );
};

export default InscannedBagsReport;
